import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { OnboardingScreenCreateRequest, OnboardingScreenEditRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useAddOnboardingScreen = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (data: OnboardingScreenCreateRequest) => api.postOnboardingScreensV1(data),
    onSuccess() {
      showFlashToast({ message: t("page.onboarding-screens.new.notification.success"), variant: "success" });
    },
    onError() {
      showFlashToast({ message: t("page.onboarding-screens.new.notification.failed"), variant: "alert" });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ONBOARDING_SCREENS_LIST(projectId) });
    },
  });
};

const useUpdateOnboardingScreen = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: { onboardingScreenId: string; data: OnboardingScreenEditRequest }) =>
      api.putOnboardingScreensV1(payload.onboardingScreenId, payload.data),
    onSuccess() {
      showFlashToast({ message: t("page.onboarding-screens.edit.notification.success"), variant: "success" });
    },
    onError() {
      showFlashToast({ message: t("page.onboarding-screens.edit.notification.failed"), variant: "alert" });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ONBOARDING_SCREENS_LIST(projectId) });
    },
  });
};

const useDeleteOnboardingScreen = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: { onboardingScreenId: string }) =>
      api.deleteOnboardingScreensByIdV1(payload.onboardingScreenId),
    onSuccess() {
      showFlashToast({
        variant: "success",
        message: t("page.onboarding-screens.actions.delete.notifications.success"),
      });
    },
    onError() {
      showFlashToast({
        variant: "alert",
        message: t("page.onboarding-screens.actions.delete.notifications.failed"),
      });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ONBOARDING_SCREENS_LIST(projectId) });
    },
  });
};

export const onboardingScreenMutations = {
  useUpdateOnboardingScreen,
  useAddOnboardingScreen,
  useDeleteOnboardingScreen,
};
